import React, { useEffect, useState } from 'react'
import { Input } from 'antd'
import { useNavigate } from 'react-router-dom';
import Loader from '../Loader';
import config, { axiosInstance } from '../Config';
import secureLocalStorage from 'react-secure-storage';
import { usePermissionData } from '../../AppContext';
import logo from "../../Assets/Images/logo.png"
import loginBg from "../../Assets/Images/bg-img5.png"

const Login = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const navigate = useNavigate();
    const { setLoginStatus, loadPermissions } = usePermissionData()

    useEffect(() => {
        const credential = secureLocalStorage.getItem('admin_credentials')
        try {
            if (credential && 'id' in credential && 'email' in credential) {
                navigate(`${config.appUrl}`);
            }
        } catch (e) {}
    }, []);

    const onHandleLogin = async (e) => {
        e.preventDefault();
        setError("");
        if (!email || !password) {
            setError("Email and password are required.");
            return;
        }
        setLoading(true);
        try {
            const response = await axiosInstance.post('auth/login', { email, password });
            const { token, data } = response.data;
            secureLocalStorage.setItem("admin_credentials", {
                id: data.userKey,
                name: data.name,
                username: data.username,
                email: data.email,
                token: token,
                role: data.role ?? null,
                profilePicture: data.profilePicture,
            });
            setLoginStatus(true);
            await loadPermissions(data.role ?? null);
            navigate(`${config.appUrl}`);
        } catch (err) {
            const msg = err?.response?.data?.message || "Invalid email or password.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mindora-login-wrapper" style={{
            backgroundImage: `url(${loginBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
        }}>
            {isLoading && <Loader />}

            {/* Blue/teal wash overlay */}
            <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(160deg, rgba(20,60,110,0.45) 0%, rgba(15,50,95,0.40) 50%, rgba(10,45,90,0.48) 100%)',
                zIndex: 0,
            }} />

            {/* Subtle sparkle dots */}
            <div className="login-bg-decor" style={{ zIndex: 1 }}>
                <div className="login-sparkle" style={{ width: 9,  height: 9,  top: '11%', left: '7%',   animationDelay: '0s' }} />
                <div className="login-sparkle" style={{ width: 6,  height: 6,  top: '23%', left: '13%',  animationDelay: '0.8s' }} />
                <div className="login-sparkle" style={{ width: 10, height: 10, top: '7%',  right: '10%', animationDelay: '1.2s' }} />
                <div className="login-sparkle" style={{ width: 5,  height: 5,  top: '29%', right: '6%',  animationDelay: '0.4s' }} />
                <div className="login-sparkle" style={{ width: 8,  height: 8,  top: '17%', right: '19%', animationDelay: '1.8s' }} />
                <div className="login-sparkle" style={{ width: 6,  height: 6,  top: '40%', left: '5%',   animationDelay: '2.4s' }} />
                <div className="login-sparkle" style={{ width: 7,  height: 7,  top: '35%', right: '4%',  animationDelay: '1.0s' }} />

                {/* Birds */}
                <svg className="login-bird login-bird-1" viewBox="-28 -13 56 18" xmlns="http://www.w3.org/2000/svg"
                    style={{ position: 'absolute', top: '13%', left: '18%', width: 55, height: 20 }}>
                    <path d="M0,0 L-3,-3 Q-11,-12 -24,-6 L-28,-4 Q-20,-10 -13,-5 Q-6,-2 0,0 Q6,-2 13,-5 Q20,-10 28,-4 L24,-6 Q11,-12 3,-3 Z"
                        fill="rgba(255,255,255,0.7)" />
                </svg>
                <svg className="login-bird login-bird-2" viewBox="-22 -11 44 15" xmlns="http://www.w3.org/2000/svg"
                    style={{ position: 'absolute', top: '8%', left: '30%', width: 42, height: 15 }}>
                    <path d="M0,0 L-2,-2 Q-8,-10 -19,-5 L-22,-3 Q-16,-9 -10,-4 Q-5,-1 0,0 Q5,-1 10,-4 Q16,-9 22,-3 L19,-5 Q8,-10 2,-2 Z"
                        fill="rgba(255,255,255,0.6)" />
                </svg>
                <svg className="login-bird login-bird-3" viewBox="-16 -9 32 12" xmlns="http://www.w3.org/2000/svg"
                    style={{ position: 'absolute', top: '10%', right: '22%', width: 32, height: 12 }}>
                    <path d="M0,0 L-2,-2 Q-6,-8 -14,-4 L-16,-3 Q-11,-7 -7,-3 Q-3,-1 0,0 Q3,-1 7,-3 Q11,-7 16,-3 L14,-4 Q6,-8 2,-2 Z"
                        fill="rgba(255,255,255,0.55)" />
                </svg>
            </div>

            {/* Login card */}
            <div className="mindora-login-container" style={{ position: 'relative', zIndex: 2 }}>

                {/* Gradient accent bar at top */}
                <div className="login-card-accent" />

                {/* Decorative background circles */}
                <div className="login-card-circle-1" />
                <div className="login-card-circle-2" />

                {/* Form body */}
                <div className="mindora-login-form-body">
                    <form onSubmit={onHandleLogin} className="mindora-login-form">

                        <div className="text-center mb-3">
                            <img src={logo} alt="Mindora" style={{ width: 190 }} />
                        </div>

                        <div className="mindora-login-header text-center">
                            <h2>Welcome Back!</h2>
                            <p>Sign in to your <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Mindora</span> admin account</p>
                        </div>

                        {error && (
                            <div className="mindora-login-error">
                                <i className="icon-warning"></i> {error}
                            </div>
                        )}

                        <div className="mindora-form-group">
                            <label>Email Address</label>
                            <Input
                                className="mindora-input"
                                type="email"
                                placeholder="admin@mindora.com"
                                value={email}
                                onChange={(e) => { setEmail(e.target.value.toLowerCase()); setError(""); }}
                            />
                        </div>

                        <div className="mindora-form-group">
                            <label>Password</label>
                            <div className="position-relative">
                                <Input
                                    className="mindora-input"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                                />
                                <div className="bd-eye-open">
                                    <a onClick={() => setShowPassword(!showPassword)} style={{ cursor: 'pointer' }}>
                                        <i className={showPassword ? "icon-eye-crossed" : "icon-eye"}></i>
                                    </a>
                                </div>
                            </div>
                        </div>

                        <button type="submit" className="mindora-login-btn">
                            Sign In
                        </button>

                    </form>
                </div>

            </div>
        </div>
    )
}

export default Login
